import { useEffect, useMemo, useRef, useState } from 'react';
import { formatSeconds, reactionsOf, STEP_ROLE_LABEL, type Media, type Message } from '@machs/shared';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { api, mediaFrameUrl, mediaPreviewUrl, mediaUrl, userAvatarUrl } from '../lib/api';
import { localMediaUrl } from '../lib/storage';
import { Icon, type IconName } from '../lib/icons';
import { avatarGradient, clock, initials } from '../lib/format';
import { Avatar, Dialog, Popover, useToast } from './ui';
import type { ReplyTarget } from './Composer';
import { FormCard } from './FormCard';
import { RichText } from './RichText';
import { ForwardDialog } from './ForwardDialog';
import { usePeople } from '../lib/people';


export function MessageBubble({ m, onReply, onEdit, showName, detailsAll = false }: {
  m: Message;
  onReply: (r: ReplyTarget) => void;
  /** Eigene Nachricht im Eingabefeld bearbeiten */
  onEdit?: (m: Message) => void;
  showName: boolean;
  /** Vorgabe aus dem Kopf des Chats: Details aller Medien offen */
  detailsAll?: boolean;
}) {
  const { client, me } = useSync();
  const plan = usePlan();
  const toast = useToast();
  const mine = m.authorId === me.user.id;
  const member = me.members.find((x) => x.userId === m.authorId);
  const author = { id: m.authorId ?? m.authorName, name: member?.name ?? m.authorName, src: member ? userAvatarUrl(member.userId, member.avatarAt) : null };
  const people = usePeople();
  const starred = client.data.taskUserState.get(`${m.taskId}:${me.user.id}`)?.starred?.includes(m.id) ?? false;
  const media = m.mediaId ? client.data.media.get(m.mediaId) ?? null : null;
  const pending = client.isPending('message', m.id) || (m.mediaId ? client.isPending('media', m.mediaId) : false);
  const replyTo = m.replyToId ? client.data.message.get(m.replyToId) : undefined;
  const replyMedia = replyTo?.mediaId ? client.data.media.get(replyTo.mediaId) : undefined;
  const quotedPoint = m.replyPoint != null ? replyMedia?.points[m.replyPoint] : undefined;
  const [seek, setSeek] = useState<Seek>(null);
  const jump = (at: number) => setSeek({ at, nonce: Date.now() });
  // Details (Standbilder, Transkript) neben dem Medium: global vorgegeben, je Nachricht umschaltbar
  const [detailsOpen, setDetailsOpen] = useState<boolean | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const reactions = reactionsOf(client, m.id, me.user.id);
  // Gäste sehen nur ihre Aufgabe: Weiterleiten geht nur im Team, Reagieren mit Schreibrecht
  const canForward = !me.guest;
  const canReact = !me.guest || me.guest.canWrite;
  const showDetails = detailsOpen ?? detailsAll;
  // Bei Link-Nachrichten steht die URL schon in der Vorschaukarte
  const displayBody = media?.kind === 'link' && media.url ? m.body.replace(media.url, '').trim() : m.body;
  const failed = media ? client.uploadError(media.id) : null;
  const auswerten = me.guest || !media
    ? undefined
    : async () => {
        try {
          await api(`/ai/media/${media.id}/retry`, { method: 'POST' });
          toast('Wird ausgewertet');
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Nicht möglich');
        }
      };

  // Wie im Messenger: an der Stelle bleibt ein leiser Hinweis stehen
  if (m.deletedAt) {
    return (
      <div className={`msg deleted ${mine ? 'out' : ''}`} data-msg={m.id}>
        {!mine && showName && <div className="who">{m.authorName}</div>}
        <p>
          <Icon name="x" size={14} />
          {mine ? 'Du hast diese Nachricht gelöscht' : 'Diese Nachricht wurde gelöscht'}
          <span className="deleted-time">{clock(m.createdAt)}</span>
        </p>
      </div>
    );
  }

  return (
    <div className={`msg ${mine ? 'out' : ''} ${reactions.length ? 'has-reactions' : ''}`} data-msg={m.id}>
      {/* Wie im Messenger: Reagieren und Weiterleiten direkt neben der Blase */}
      {(canReact || canForward) && !pending && (
        <div className="hoveracts">
          {canReact && (
            <Popover
              align={mine ? 'right' : 'left'}
              width={318}
              trigger={({ toggle }) => (
                <button className="hoverbtn" onClick={toggle} aria-label="Reagieren" data-tip="Reagieren"><Icon name="smile" size={18} /></button>
              )}
            >
              {(close) => <ReactionRow mine={reactions.find((r) => r.mine)?.emoji} onPick={(e) => (void client.toggleReaction(m.id, e), close())} />}
            </Popover>
          )}
          {canForward && (
            <button className="hoverbtn" onClick={() => setForwarding(true)} aria-label="Weiterleiten" data-tip="Weiterleiten"><Icon name="forward" size={18} /></button>
          )}
        </div>
      )}
      {m.forwarded && (
        <span className="fwdlabel"><Icon name="forward" size={13} />Weitergeleitet</span>
      )}
      {!mine && showName && (
        <div className="who" style={{ backgroundImage: avatarGradient(m.authorId ?? m.authorName), WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
          {m.authorName}
          {m.authorKind === 'guest' && <span className="ml-1.5 rounded-md bg-[var(--surface2)] px-1.5 text-[10.5px] text-[var(--muted)]">extern</span>}
        </div>
      )}
      {m.kind === 'rueckfrage' && (
        <span className="role" style={{ color: 'var(--brand-a)', background: 'color-mix(in srgb, var(--brand-a) 12%, transparent)' }}>
          <Icon name="question" size={13} />Rückfrage{m.questionOpen ? '' : ', beantwortet'}
        </span>
      )}
      {replyTo && (
        <button className="quote" onClick={() => zeigeNachricht(replyTo.id)} aria-label={`Zur Nachricht von ${replyTo.authorName} springen`}>
          <b className="block text-[var(--text)]">{replyTo.authorName}</b>
          {replyTo.deletedAt ? 'Nachricht gelöscht' : quotedPoint ? `${formatSeconds(quotedPoint.at)} ${quotedPoint.text}` : (replyTo.body || 'Medium').slice(0, 140)}
        </button>
      )}

      {media && hasDetails(media) && (
        /* Reiter an der Blase: klappt die Details zur Seite aus */
        <button
          className="detailarrow"
          onClick={() => setDetailsOpen(!showDetails)}
          aria-expanded={showDetails}
          aria-label={showDetails ? 'Standbilder und Transkript ausblenden' : 'Standbilder und Transkript zeigen'}
          data-tip={showDetails ? 'Ausblenden' : 'Standbilder und Transkript'}
        >
          <Icon name="chev" size={14} style={{ transform: (mine ? !showDetails : showDetails) ? 'rotate(180deg)' : undefined, transition: 'transform .2s' }} />
        </button>
      )}
      {media && (
        hasDetails(media) ? (
          <div className={`withdetails ${mine ? 'out' : ''}`}>
            {showDetails && <MediaDetails media={media} onSeek={jump} onAuswerten={auswerten} />}
            <div className="min-w-0">
              <MediaView media={media} seek={seek} author={author} mine={mine} />
            </div>
          </div>
        ) : (
          <MediaView media={media} seek={seek} author={author} mine={mine} />
        )
      )}

      {displayBody && <RichText text={displayBody} mentions={people.filter((p) => (m.mentions ?? []).includes(p.id))} me={me.user.id} />}
      {m.form && <FormCard m={m} />}
      {media && !failed && (
        <ContentCard
          media={media}
          onSeek={jump}
          onReplyPoint={(i) => onReply({ message: m, point: i })}
          onRetry={auswerten}
          onPointTask={me.guest && !me.guest.projectId ? undefined : async (i) => {
            const task = client.data.task.get(m.taskId);
            const pt = media.points[i];
            if (!task || !pt || !plan.more('openTasks')) return;
            const id = await client.saveTask({ projectId: task.projectId, title: pt.text.slice(0, 200) });
            await client.sendMessage({ taskId: id, kind: 'text', body: `${pt.text}\n\nAus „${task.title}“ (${formatSeconds(pt.at)})` });
            toast('Neue Aufgabe angelegt');
          }}
        />
      )}
      {failed && media && (
        <button className="check mt-1.5 w-full text-left" onClick={() => void client.retry(media.id)} data-tip="Erneut senden">
          <Icon name="alert" size={17} />
          <span className="min-w-0 flex-1">
            <span className="block">Nicht gesendet</span>
            <span className="block truncate text-xs font-normal text-[var(--muted)]">{failed}</span>
          </span>
          <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[var(--problem)] text-white"><Icon name="reset" size={15} /></span>
        </button>
      )}

      <div className="meta">
        <span className="tools">
        <Popover align={mine ? 'right' : 'left'} trigger={({ toggle }) => (
          <button className="menuarrow" onClick={toggle} aria-label="Aktionen zur Nachricht"><Icon name="down" size={16} /></button>
        )}>
          {(close) => (
            <>
              {pending && (
                <button className="item" onClick={() => (void client.retry(m.mediaId ?? undefined), close())}><Icon name="reset" size={18} />Erneut senden</button>
              )}
              <button className="item" onClick={() => (onReply({ message: m, point: null }), close())}><Icon name="reply" size={18} />Antworten</button>
              {canForward && <button className="item" onClick={() => (setForwarding(true), close())}><Icon name="forward" size={18} />Weiterleiten</button>}
              <button className="item" onClick={() => (void client.updateMessage(m.id, { isMilestone: !m.isMilestone }), close())}>
                <Icon name="flag" size={18} />{m.isMilestone ? 'Kein Meilenstein mehr' : 'Als Meilenstein markieren'}
              </button>
              {m.kind === 'rueckfrage' && (
                <button className="item" onClick={() => (void client.updateMessage(m.id, { questionOpen: !m.questionOpen }), close())}>
                  <Icon name="question" size={18} />{m.questionOpen ? 'Als beantwortet markieren' : 'Wieder offen'}
                </button>
              )}
              {m.body && (
                <button className="item" onClick={() => (void navigator.clipboard?.writeText(m.body), close())}><Icon name="text" size={18} />Text kopieren</button>
              )}
              {!me.guest && (
                <button className="item" onClick={() => (void client.toggleStar(m.id), close())}>
                  <Icon name="star" size={18} />{starred ? 'Markierung entfernen' : 'Markieren'}
                </button>
              )}
              {mine && onEdit && !pending && m.kind !== 'rueckfrage' && (m.body || !m.mediaId) && (
                <button className="item" onClick={() => (onEdit(m), close())}><Icon name="edit" size={18} />Bearbeiten</button>
              )}
              {mine && (
                <button className="item text-[var(--problem)]" onClick={() => (setConfirmDelete(true), close())}><Icon name="trash" size={18} />Löschen</button>
              )}
            </>
          )}
        </Popover>
        </span>
        {starred && <Icon name="starFill" size={12} className="text-[var(--milestone)]" aria-label="Markiert" />}
        {m.isMilestone && <Icon name="flag" size={13} className="text-[var(--milestone)]" />}
        {m.editedAt && <span className="italic">bearbeitet</span>}
        {clock(m.createdAt)}
        {mine && <Icon name={pending ? 'check' : 'checks'} size={15} style={{ color: pending ? 'var(--dim)' : 'var(--brand-b)' }} aria-label={pending ? 'Wartet auf Abgleich' : 'Übertragen'} />}
      </div>

      {reactions.length > 0 && (
        <Popover
          align={mine ? 'right' : 'left'}
          width={260}
          trigger={({ toggle }) => (
            <button className="reactpill" onClick={toggle} data-tip={reactions.map((r) => `${r.emoji} ${r.names.join(', ')}`).join(' · ')} data-tip-wrap aria-label="Reaktionen">
              {reactions.slice(0, 3).map((r) => <span key={r.emoji}>{r.emoji}</span>)}
              {reactions.reduce((n, r) => n + r.count, 0) > 1 && <b>{reactions.reduce((n, r) => n + r.count, 0)}</b>}
            </button>
          )}
        >
          {(close) => (
            <div className="p-1">
              {reactions.map((r) => (
                <button
                  key={r.emoji}
                  className="item"
                  disabled={!r.mine}
                  onClick={() => (void client.toggleReaction(m.id, r.emoji), close())}
                >
                  <span className="text-[20px] leading-none">{r.emoji}</span>
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-[13.5px]">{r.names.join(', ')}</b>
                    {r.mine && <small className="text-[11.5px] text-[var(--muted)]">Tippen zum Entfernen</small>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Popover>
      )}
      {forwarding && <ForwardDialog message={m} onClose={() => setForwarding(false)} />}
      {confirmDelete && (
        <Dialog title="Nachricht löschen?" onClose={() => setConfirmDelete(false)}>
          <p className="m-0 text-[14px] text-[var(--muted)]">
            {media ? 'Nachricht und Datei werden für alle entfernt.' : 'Die Nachricht wird für alle entfernt.'} Im Verlauf bleibt ein Hinweis stehen.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>Abbrechen</button>
            <button className="btn btn-danger" data-autofocus onClick={() => (setConfirmDelete(false), void client.deleteMessage(m.id))}>Löschen</button>
          </div>
        </Dialog>
      )}

    </div>
  );
}

/** Zur zitierten Nachricht blättern und sie kurz hervorheben. */
function zeigeNachricht(id: string) {
  const el = document.querySelector(`[data-msg="${id}"]`);
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.classList.add('flash');
  setTimeout(() => el.classList.remove('flash'), 1600);
}

/** Sprungziel mit Kennnummer, damit derselbe Zeitpunkt erneut anspringt. */
type Seek = { at: number; nonce: number } | null;

/** Standbilder des Mediums; ältere Einträge im lokalen Bestand kennen das Feld noch nicht. */
function framesOf(media: Media): number[] {
  return media.frames ?? [];
}

/**
 * Hat das Medium einen Detailbereich? Bei Sprache und Video immer, damit man
 * sieht, ob ein Transkript vorliegt — sonst nur mit Standbildern oder Text.
 */
function hasDetails(media: Media): boolean {
  if (media.kind === 'sprache' || media.kind === 'video') return true;
  return framesOf(media).length > 0 || !!media.transcript;
}

function src(media: Media): string {
  return localMediaUrl(media.id) ?? mediaUrl(media.id);
}

function MediaView({ media, seek, author, mine }: { media: Media; seek: Seek; author?: { id: string; name: string; src: string | null }; mine?: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [bereit, setBereit] = useState(false);
  useEffect(() => {
    if (seek && videoRef.current) {
      videoRef.current.currentTime = seek.at;
      void videoRef.current.play().catch(() => {});
    }
  }, [seek]);

  switch (media.kind) {
    case 'bild':
      return (
        <a className="media block" href={src(media)} target="_blank" rel="noreferrer">
          <img src={src(media)} alt={media.summary ?? media.fileName ?? 'Foto'} loading="lazy" />
        </a>
      );
    case 'video': {
      // Platz vorab reservieren: die Maße kennt der Server, sonst hilft das
      // erste Standbild als Vorschau — geraten wird nichts.
      const masse = media.width && media.height ? `${media.width} / ${media.height}` : undefined;
      const poster = framesOf(media).length > 0 ? mediaFrameUrl(media.id, 0) : undefined;
      return (
        <div className="media video" style={masse ? { aspectRatio: masse } : undefined}>
          {!bereit && <span className="skeleton" aria-hidden="true" />}
          <video
            ref={videoRef}
            src={src(media)}
            poster={poster}
            controls
            preload="metadata"
            playsInline
            style={{ opacity: bereit ? 1 : 0 }}
            onLoadedMetadata={() => setBereit(true)}
            onLoadedData={() => setBereit(true)}
            onError={() => setBereit(true)}
          />
        </div>
      );
    }
    case 'sprache':
      return <VoicePlayer media={media} seek={seek} author={author} mine={mine} />;
    case 'link':
      return (
        <a className="linkcard" href={media.url ?? '#'} target="_blank" rel="noreferrer noopener">
          {/* Vorschaubild wie im Messenger; ohne Bild nur bei Videos ein Platzhalter */}
          {media.linkImage ? (
            <div className="limage" style={{ aspectRatio: media.width && media.height ? `${media.width} / ${media.height}` : '1.91 / 1' }}>
              <img src={mediaPreviewUrl(media.id, media.updatedAt)} alt="" loading="lazy" />
              {isVideoUrl(media.url) && <span className="lplay"><Icon name="play" size={26} /></span>}
            </div>
          ) : isVideoUrl(media.url) ? (
            <div className="lthumb"><Icon name="play" size={34} /></div>
          ) : null}
          <div className="px-3 py-2">
            <b className="lt-title">{media.linkTitle ?? media.url}</b>
            {media.linkDescription && <span className="lt-desc">{media.linkDescription}</span>}
            <small className="mt-0.5 flex items-center gap-1 text-[11.5px] text-[var(--muted)]">
              <Icon name={isMapUrl(media.url) ? 'place' : 'link'} size={12} />
              {isMapUrl(media.url) ? 'Karte © OpenStreetMap-Mitwirkende' : media.linkSite}
            </small>
          </div>
        </a>
      );
    default:
      return (
        <a className="linkcard flex items-center gap-3 p-3" href={src(media)} target="_blank" rel="noreferrer" download={media.fileName ?? undefined}>
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--surface2)] text-[var(--brand-a)]"><Icon name="file" size={20} /></span>
          <span className="min-w-0">
            <b className="block truncate text-[13.5px]">{media.fileName ?? 'Datei'}</b>
            {media.size ? <small className="text-[var(--muted)]">{(media.size / 1024 / 1024).toFixed(1)} MB</small> : null}
          </span>
        </a>
      );
  }
}

function bars(seed: string, n = 32): number[] {
  let h = 7;
  return Array.from({ length: n }, (_, i) => {
    h = (h * 31 + seed.charCodeAt(i % seed.length)) >>> 0;
    return 6 + (h % 22);
  });
}

function VoicePlayer({ media, seek, author, mine }: { media: Media; seek: Seek; author?: { id: string; name: string; src: string | null }; mine?: boolean }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(media.durationSec ?? 0);
  const [rate, setRate] = useState(1);
  // Mit Profilbild wie im Messenger dichter und über die ganze Breite
  const heights = bars(media.id, author ? 44 : 32);

  useEffect(() => {
    if (seek && audio.current) {
      audio.current.currentTime = seek.at;
      void audio.current.play();
    }
  }, [seek]);

  /** Stelle aus der Zeigerposition in der Wellenform. */
  function seekTo(clientX: number, el: HTMLElement) {
    const r = el.getBoundingClientRect();
    const share = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    if (audio.current && dur) {
      audio.current.currentTime = share * dur;
      setPos(share * dur);
    }
  }

  function step(seconds: number) {
    if (!audio.current) return;
    audio.current.currentTime = Math.min(dur, Math.max(0, audio.current.currentTime + seconds));
    setPos(audio.current.currentTime);
  }

  /** 1× → 1,5× → 2× und zurück */
  function nextRate() {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audio.current) audio.current.playbackRate = next;
  }

  const progress = dur ? pos / dur : 0;
  // Wie im Messenger: Profilbild mit Mikrofon; beim Abspielen steht dort das Tempo
  const face = author && (
    <span className="vface">
      {playing || rate !== 1 ? (
        <button className="rate big" onClick={nextRate} aria-label={`Tempo ${rate.toString().replace('.', ',')}-fach, umschalten`}>
          {rate.toString().replace('.', ',')}×
        </button>
      ) : (
        <>
          <Avatar name={initials(author.name)} gradient={avatarGradient(author.id)} src={author.src} size={44} />
          <span className="vmic"><Icon name="mic" size={11} /></span>
        </>
      )}
    </span>
  );
  return (
    <div className={`voice my-1 ${author ? 'withface' : ''}`}>
      {mine && face}
      <audio
        ref={audio}
        src={src(media)}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => (setPlaying(false), setPos(0))}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration);
          e.currentTarget.playbackRate = rate;
        }}
      />
      <button className="pl" onClick={() => (playing ? audio.current?.pause() : void audio.current?.play())} aria-label={playing ? 'Pause' : 'Abspielen'}>
        <Icon name={playing ? 'pause' : 'play'} size={author ? 22 : 16} />
      </button>
      <span className="vtrack">
      <div
        className="wave"
        role="slider"
        aria-label="Position"
        aria-valuemin={0}
        aria-valuemax={Math.round(dur)}
        aria-valuenow={Math.round(pos)}
        tabIndex={0}
        // Tippen und Ziehen springt an die Stelle
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          seekTo(e.clientX, e.currentTarget);
        }}
        onPointerMove={(e) => e.currentTarget.hasPointerCapture(e.pointerId) && seekTo(e.clientX, e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') (e.preventDefault(), step(5));
          if (e.key === 'ArrowLeft') (e.preventDefault(), step(-5));
        }}
      >
        {heights.map((h, i) => <i key={i} className={i / heights.length < progress ? 'on' : ''} style={{ height: h }} />)}
        {author && <b className="knob" style={{ left: `${progress * 100}%` }} />}
      </div>
      <time className="text-[11.5px] text-[var(--muted)] tabular-nums">{formatSeconds(playing || pos ? pos : dur)}</time>
      </span>
      {!author && (
        <button className="rate" onClick={nextRate} aria-label={`Tempo ${rate.toString().replace('.', ',')}-fach, umschalten`}>
          {rate.toString().replace('.', ',')}×
        </button>
      )}
      {!mine && face}
    </div>
  );
}

/**
 * Details neben dem Medium: Standbilder über das Video verteilt und das
 * Transkript zum Durchsuchen. Ein Klick springt an die Stelle.
 */
function MediaDetails({ media, onSeek, onAuswerten }: { media: Media; onSeek: (at: number) => void; onAuswerten?: () => void }) {
  const [suche, setSuche] = useState('');
  const zeilen = useMemo(() => {
    if (!media.transcript) return [];
    return media.transcript.split('\n').map((line) => {
      const hit = line.match(/^\[(\d+):(\d{2})(?::(\d{2}))?\]\s*(.*)$/);
      if (!hit) return { at: null as number | null, label: '', text: line };
      const at = hit[3] ? Number(hit[1]) * 3600 + Number(hit[2]) * 60 + Number(hit[3]) : Number(hit[1]) * 60 + Number(hit[2]);
      return { at, label: line.slice(1, line.indexOf(']')), text: hit[4] ?? '' };
    });
  }, [media.transcript]);

  const begriff = suche.trim().toLowerCase();
  const treffer = begriff ? zeilen.filter((z) => z.text.toLowerCase().includes(begriff)) : zeilen;

  return (
    <aside className="mdetails">
      {framesOf(media).length > 0 && (
        <div className="frames">
          {framesOf(media).map((at, i) => (
            <button key={i} onClick={() => onSeek(at)} aria-label={`Zu ${formatSeconds(at)} springen`} data-tip={formatSeconds(at)}>
              <img src={mediaFrameUrl(media.id, i)} alt="" loading="lazy" />
              <time>{formatSeconds(at)}</time>
            </button>
          ))}
        </div>
      )}
      {!media.transcript && (
        <div className="px-1 py-1.5">
          <p className="m-0 text-[12.5px] text-[var(--muted)]">
            {media.status === 'verarbeiten'
              ? 'Wird ausgewertet …'
              : media.aiError
                ? `Kein Transkript: ${media.aiError}`
                : 'Noch kein Transkript.'}
          </p>
          {media.status !== 'verarbeiten' && onAuswerten && (
            <button className="chip mt-2" onClick={onAuswerten}>
              <Icon name="sparkle" size={14} />Transkript erzeugen
            </button>
          )}
        </div>
      )}
      {media.transcript && (
        <>
          <label className="tsearch">
            <Icon name="search" size={13} />
            <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Im Transkript suchen" aria-label="Im Transkript suchen" />
            {suche && (
              <button onClick={() => setSuche('')} aria-label="Suche leeren"><Icon name="x" size={13} /></button>
            )}
          </label>
          <div className="tscript scroll">
            {treffer.length === 0 && <p className="m-0 py-2 text-center text-[var(--dim)]">Nichts gefunden</p>}
            {treffer.map((z, i) => (
              <p key={i} className="m-0 flex gap-2">
                {z.at != null && (
                  <button className="flex-none font-semibold tabular-nums text-[var(--brand-b)]" onClick={() => onSeek(z.at!)}>{z.label}</button>
                )}
                <span>{mark(z.text, begriff)}</span>
              </p>
            ))}
          </div>
        </>
      )}
    </aside>
  );
}

/** Treffer im Text hervorheben. */
function mark(text: string, begriff: string) {
  if (!begriff) return text;
  const teile = text.split(new RegExp(`(${begriff.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));
  return teile.map((teil, i) => (teil.toLowerCase() === begriff ? <mark key={i}>{teil}</mark> : <span key={i}>{teil}</span>));
}

/** Inhaltskarte: Kurzfassung, Inhaltspunkte mit Zeitmarken, Transkript. */
function ContentCard({ media, onSeek, onReplyPoint, onRetry, onPointTask }: {
  media: Media;
  onSeek: (s: number) => void;
  onReplyPoint: (i: number) => void;
  onRetry?: () => void;
  onPointTask?: (i: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const hasContent = media.summary || media.points.length > 0 || media.transcript || media.imageText;

  if (media.status === 'hochladen') {
    return <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--muted)]"><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />Wird übertragen</div>;
  }
  if (media.status === 'verarbeiten') {
    return <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--muted)]"><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />Wird ausgewertet</div>;
  }
  if (media.aiError && !hasContent) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--muted)]">
        <Icon name="alert" size={14} />
        <span className="min-w-0 flex-1 truncate" data-tip={media.aiError} data-tip-wrap="">Auswertung fehlgeschlagen: {media.aiError}</span>
        {onRetry && <button className="font-semibold text-[var(--brand-a)]" onClick={onRetry}>Erneut</button>}
      </div>
    );
  }
  if (!hasContent) return null;

  if (media.kind === 'bild') {
    return (
      <div className="mt-1.5 flex items-start gap-1.5 text-[13.5px]">
        <Icon name="sparkle" size={15} className="mt-0.5 text-[var(--brand-a)]" />
        <span>{media.summary}{media.imageText ? <span className="block text-[var(--muted)]">Text im Bild: {media.imageText}</span> : null}</span>
      </div>
    );
  }

  return (
    <div className="ai">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[var(--muted)]">
        <Icon name="sparkle" size={14} className="text-[var(--brand-a)]" />Worum es geht
        {media.points.length > 0 && <span className="ml-auto font-medium">{media.points.length} {media.points.length === 1 ? 'Punkt' : 'Punkte'}</span>}
      </div>
      {media.summary && <p className="mb-2 text-[13.5px]">{media.summary}</p>}
      {media.points.length > 0 && (
        <ol className="pts">
          {media.points.map((pt, i) => (
            <li key={i}>
              <button onClick={() => onSeek(pt.at)} aria-label={`Zu ${formatSeconds(pt.at)} springen`}><time>{formatSeconds(pt.at)}</time></button>
              <span className="flex-1">{pt.text}</span>
              <button className="iconbtn" style={{ width: 26, height: 26, borderRadius: 8 }} onClick={() => onReplyPoint(i)} aria-label="Auf diesen Punkt antworten" data-tip="Antworten">
                <Icon name="reply" size={14} />
              </button>
              {onPointTask && (
                <button className="iconbtn" style={{ width: 26, height: 26, borderRadius: 8 }} onClick={() => onPointTask(i)} aria-label="Als neue Aufgabe anlegen" data-tip="Als Aufgabe">
                  <Icon name="plus" size={14} />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
      {media.transcript && (
        <div className="mt-1.5 border-t border-[var(--line)] pt-1.5">
          <button className="flex items-center gap-1 text-xs font-semibold text-[var(--brand-b)]" onClick={() => setOpen(!open)} aria-expanded={open}>
            <Icon name="chev" size={14} style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform .2s' }} />Transkript
          </button>
          {open && (
            <div className="mt-1 flex flex-col gap-0.5 text-[13.5px] text-[var(--muted)]">
              {media.transcript.split('\n').map((line, i) => {
                const hit = line.match(/^\[(\d+):(\d{2})(?::(\d{2}))?\]\s*(.*)$/);
                if (!hit) return <p key={i} className="m-0">{line}</p>;
                const secs = hit[3] ? Number(hit[1]) * 3600 + Number(hit[2]) * 60 + Number(hit[3]) : Number(hit[1]) * 60 + Number(hit[2]);
                return (
                  <p key={i} className="m-0 flex gap-2">
                    <button className="flex-none font-semibold tabular-nums text-[var(--brand-b)]" onClick={() => onSeek(secs)}>{line.slice(1, line.indexOf(']'))}</button>
                    <span>{hit[4]}</span>
                  </p>
                );
              })}
            </div>
          )}
          {media.aiError && onRetry && (
            <button className="mt-1 text-xs font-semibold text-[var(--brand-a)]" onClick={onRetry}>Erneut auswerten</button>
          )}
        </div>
      )}
    </div>
  );
}

/** Video-Links (Loom, YouTube, …) zeigen ein Abspiel-Zeichen auf der Vorschau. */
function isVideoUrl(url: string | null): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return /(^|\.)(loom\.com|youtube\.com|youtu\.be|vimeo\.com|instagram\.com|facebook\.com|fb\.watch)$/i.test(u.hostname) || /\.(mp4|webm|mov|m4v)$/i.test(u.pathname);
  } catch {
    return false;
  }
}

/** Standort-Link (OpenStreetMap mit Markierung). */
function isMapUrl(url: string | null): boolean {
  return !!url && /^https:\/\/(www\.)?openstreetmap\.org\/\?mlat=/.test(url);
}

// Schnelle Reaktionen wie im Messenger, dazu der Haken für Aufgaben
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '✅'];

function ReactionRow({ mine, onPick }: { mine?: string; onPick: (emoji: string) => void }) {
  return (
    <div className="reactrow" role="group" aria-label="Reaktion wählen">
      {QUICK_REACTIONS.map((e) => (
        <button key={e} aria-pressed={mine === e} onClick={() => onPick(e)} aria-label={`Mit ${e} reagieren`}>{e}</button>
      ))}
    </div>
  );
}
